/**
 * MapaPage (M10, RF-M10-002, RN-M10-003) — distribuição geográfica + mapa.
 *
 * Contrato congelado: GET /painel/mapa é req{—} (sem parâmetros). A página busca
 * o agregado completo uma vez e aplica os filtros (UF/resultado) CLIENT-SIDE
 * sobre `pontos` e `porUf`. O mapa é renderizado no cliente (MapaVisitas, SVG) e
 * só contém visitas com coordenada válida (o backend já exclui geom nula/ERRO —
 * PC-02). Clicar num ponto navega para a ficha da escola (rota do M7). Acesso
 * universal de leitura.
 *
 * Contorno das UFs (spec mapa-contorno-brasil, design §7; ADR-032): carregado sob
 * demanda (chunk próprio) por `useContornoUfs`, sem bloquear os pontos — que
 * aparecem sobre o fundo liso e ganham o contorno quando ele chega. O filtro de UF
 * também enquadra a UF no mapa (só sigla existente no contorno); limpar o filtro
 * volta ao Brasil inteiro. Falha do contorno → aviso, pontos e clique continuam.
 * ADR-038: o filtro Evidência (client-side, como UF e resultado) sobre a `evidencia` de cada ponto.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapaVisitas } from '../components/MapaVisitas';
import { useContornoUfs, useMapa } from '../hooks/usePainel';
import { PontoMapa } from '../services/painel-types';
import { Evidencia } from '../services/consulta-types';
import { FiltroEvidencia } from '../components/Evidencia';

export function MapaPage(): JSX.Element {
  const navigate = useNavigate();
  const [ufInput, setUfInput] = useState('');
  const [resultadoInput, setResultadoInput] = useState('');
  const [evidenciaInput, setEvidenciaInput] = useState<Evidencia | undefined>(undefined);
  const [filtros, setFiltros] = useState<{ uf?: string; resultado?: string; evidencia?: Evidencia }>({});
  const { mapa, carregando, erro } = useMapa();
  const { contorno, erro: erroContorno } = useContornoUfs();

  function aplicar(): void {
    setFiltros({
      ...(ufInput.trim() ? { uf: ufInput.trim().toUpperCase() } : {}),
      ...(resultadoInput ? { resultado: resultadoInput } : {}),
      ...(evidenciaInput ? { evidencia: evidenciaInput } : {}),
    });
  }

  const indisponivel = erro;

  // Filtragem client-side: o de resultado restringe os pontos; o de UF restringe a
  // tabela porUf e enquadra a UF no mapa (o ponto não carrega UF — os pontos fora
  // do quadro da UF simplesmente não são desenhados; ADR-032).
  const pontosFiltrados = useMemo<readonly PontoMapa[]>(() => {
    let pontos = mapa?.pontos ?? [];
    if (filtros.resultado) {
      const alvo = filtros.resultado.toLowerCase();
      pontos = pontos.filter((p) => (p.resultado ?? '').toLowerCase() === alvo);
    }
    if (filtros.evidencia) {
      // Ponto sem a evidência (API anterior) é de termo digitalizado.
      pontos = pontos.filter((p) => (p.evidencia ?? 'imagem') === filtros.evidencia);
    }
    return pontos;
  }, [mapa, filtros.resultado, filtros.evidencia]);

  // Enquadramento só para sigla que existe no contorno (desconhecida → Brasil inteiro).
  const ufEmFoco = filtros.uf && contorno?.ufs.some((u) => u.sigla === filtros.uf) ? filtros.uf : undefined;

  const porUf = mapa?.porUf ?? {};
  const ufs = useMemo<string[]>(() => {
    const todas = Object.keys(porUf).sort();
    if (!filtros.uf) {
      return todas;
    }
    return todas.filter((u) => u === filtros.uf);
  }, [porUf, filtros.uf]);

  return (
    <div>
      <h1 className="page-title">Mapa de visitas</h1>
      <p className="page-subtitle">
        Cobertura territorial das visitas georreferenciadas. Clique em um ponto para abrir a ficha
        da escola.
      </p>

      <section className="card">
        <div className="filtros">
          <div className="field">
            <label htmlFor="mapa-uf">UF</label>
            <input id="mapa-uf" type="text" maxLength={2} value={ufInput} onChange={(e) => setUfInput(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="mapa-resultado">Resultado</label>
            <select id="mapa-resultado" value={resultadoInput} onChange={(e) => setResultadoInput(e.target.value)}>
              <option value="">(qualquer)</option>
              <option value="produtiva">Produtiva</option>
              <option value="improdutiva">Improdutiva</option>
            </select>
          </div>
          <FiltroEvidencia
            id="mapa-evidencia"
            semReferencias
            valor={{ evidencia: evidenciaInput }}
            onMudar={(v) => setEvidenciaInput(v.evidencia)}
          />
          <div className="field">
            <label>&nbsp;</label>
            <button type="button" className="btn btn-primary" onClick={aplicar}>
              Aplicar filtros
            </button>
          </div>
        </div>
      </section>

      {carregando && <p className="muted">Carregando…</p>}

      {indisponivel ? (
        <p className="notice">O mapa está temporariamente indisponível.</p>
      ) : (
        mapa && (
          <>
            <section className="card">
              <h2>Mapa</h2>
              <MapaVisitas
                pontos={pontosFiltrados}
                onSelecionar={(inep) => navigate(`/escolas/${inep}`)}
                contorno={contorno}
                contornoIndisponivel={erroContorno}
                ufEmFoco={ufEmFoco}
              />
            </section>

            <section className="card">
              <h2>Distribuição por UF</h2>
              {ufs.length === 0 ? (
                <p className="muted">Sem dados para os filtros aplicados.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>UF</th>
                      <th>Visitas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ufs.map((u) => (
                      <tr key={u}>
                        <td>{u}</td>
                        <td>{new Intl.NumberFormat('pt-BR').format(porUf[u])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )
      )}
    </div>
  );
}
